import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { DrawerWithCloseButton } from '@/components/ui/drawer'
import {
  type BuyerType,
  type ClosedLostDetails,
  LeadSource,
  type SalesLeadFilters,
  type SalesLeadListItem,
  salesLeadStageOrder,
  SalesLeadStatus,
  useMovePipelineLead,
  useSalesLeadStats,
} from '@/models/salesLead'
import { ClosedLostReasonDialog } from '../ClosedLostReasonDialog'
import { LeadFilterBar } from '../LeadFilterBar'
import { SalesLeadDetail } from '../SalesLeadDetail'
import { PipelineCardContent } from './PipelineCard'
import { PipelineColumn } from './PipelineColumn'

const LIST_PATH = '/insure/internal/sales-leads'

export function PipelineBoard() {
  const [searchParams, setSearchParams] = useSearchParams()

  // The `lead` param drives the right-side detail drawer (set by clicking a card).
  const openLeadId = searchParams.get('lead')
  function closeDrawer() {
    const next = new URLSearchParams(searchParams)
    next.delete('lead')
    setSearchParams(next, { replace: true })
  }
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const [activeLead, setActiveLead] = useState<SalesLeadListItem | null>(null)
  // A card dropped on Closed/Lost waits here, unmoved, until the rep says why.
  const [losingLead, setLosingLead] = useState<SalesLeadListItem | null>(null)

  // Bound the board to the viewport so the COLUMNS scroll (cards vertically,
  // row horizontally) instead of the whole shell — without touching any layout.
  // An explicit width <= viewport can't force the flex `main`/navbar wider.
  const boardRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<{ width?: number; height?: number }>({})
  useEffect(() => {
    function update() {
      const rect = boardRef.current?.getBoundingClientRect()
      if (!rect) return
      setBox((prev) => {
        // Reserve the real space left around us: 32px on the right (the two px-4
        // wrappers — `main` + `Container`) and 24px at the bottom (the `pb-6` on
        // the content wrapper). Measuring against documentElement.clientWidth/
        // Height (not window.inner*, which include the scrollbars) keeps the board
        // exactly inside its container instead of overshooting by ~a scrollbar's
        // width into a sliver horizontal/vertical page scrollbar.
        const width = document.documentElement.clientWidth - rect.left - 32
        const height = document.documentElement.clientHeight - rect.top - 24
        if (prev.width === width && prev.height === height) return prev
        return { width, height }
      })
    }
    update()
    window.addEventListener('resize', update)
    const observer = new ResizeObserver(update)
    const parent = boardRef.current?.parentElement
    if (parent) observer.observe(parent)
    return () => {
      window.removeEventListener('resize', update)
      observer.disconnect()
    }
  }, [])

  // Filters come from the URL (written by LeadFilterBar) — same params as the
  // table view. `status` is excluded: the columns already are the statuses.
  const filters: Omit<SalesLeadFilters, 'status' | 'page'> = {
    search: searchParams.get('search') ?? undefined,
    assignedRep: searchParams.get('rep') ?? undefined,
    source: (searchParams.get('source') as LeadSource | null) ?? undefined,
    state: searchParams.get('state') ?? undefined,
    minScore: searchParams.get('min_score') ? Number(searchParams.get('min_score')) : undefined,
    safetyRating: searchParams.get('safety_rating') ?? undefined,
    buyerType: (searchParams.get('buyer_type') as BuyerType | null) ?? undefined,
    minUnits: searchParams.get('min_units') ? Number(searchParams.get('min_units')) : undefined,
    maxUnits: searchParams.get('max_units') ? Number(searchParams.get('max_units')) : undefined,
    expiringWithinDays: searchParams.get('expiring') ? Number(searchParams.get('expiring')) : undefined,
    notExpiringWithinDays: searchParams.get('not_expiring')
      ? Number(searchParams.get('not_expiring'))
      : undefined,
    unread: searchParams.get('unread') === '1',
    followUp: searchParams.get('followup') === '1',
    tags: searchParams.get('tags') ?? undefined,
    excludeTags: searchParams.get('exclude_tags') ?? undefined,
    eligibility: searchParams.get('eligibility') ?? undefined,
  }

  const { data: stats } = useSalesLeadStats(filters)
  const move = useMovePipelineLead(filters)

  function handleDragStart(event: DragStartEvent) {
    setActiveLead((event.active.data.current?.lead as SalesLeadListItem | undefined) ?? null)
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveLead(null)
    const { active, over } = event
    if (!over) return
    const targetStatus = over.id as SalesLeadStatus
    const lead = active.data.current?.lead as SalesLeadListItem | undefined
    if (!lead || lead.status === targetStatus) return
    if (targetStatus === SalesLeadStatus.CLOSED_LOST) {
      setLosingLead(lead)
      return
    }
    moveLead(lead, targetStatus)
  }

  function moveLead(lead: SalesLeadListItem, targetStatus: SalesLeadStatus, details?: ClosedLostDetails) {
    move.mutate(
      { id: lead.id, lead, targetStatus, ...details },
      { onError: () => toast.error('Could not move lead — please try again') }
    )
  }

  return (
    <div
      ref={boardRef}
      style={{ width: box.width, height: box.height }}
      className="flex flex-col overflow-hidden pb-2"
    >
      <div className="mb-3 flex items-center gap-3">
        <div className="text-2xl font-semibold">Pipeline</div>
        <Link to={`${LIST_PATH}?${searchParams.toString()}`} className="ml-auto">
          <Button variant="outline" className="h-9">
            Table view
          </Button>
        </Link>
      </div>
      <div className="mb-3">
        <LeadFilterBar showStatus={false} />
      </div>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-2">
          {salesLeadStageOrder.map((status) => (
            <PipelineColumn
              key={status}
              status={status}
              count={stats?.byStatus[status] ?? 0}
              filters={filters}
            />
          ))}
        </div>
        {/* No drop animation: the card is already moved optimistically, so the
            default "fly back to origin then fade" looks wrong. */}
        <DragOverlay dropAnimation={null}>
          {activeLead ? (
            <div className="w-72 rotate-2 opacity-90">
              <PipelineCardContent lead={activeLead} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <ClosedLostReasonDialog
        open={losingLead !== null}
        onCancel={() => setLosingLead(null)}
        onConfirm={(details) => {
          if (losingLead) moveLead(losingLead, SalesLeadStatus.CLOSED_LOST, details)
          setLosingLead(null)
        }}
      />

      <DrawerWithCloseButton
        open={!!openLeadId}
        onOpenChange={(open) => !open && closeDrawer()}
        handleClose={closeDrawer}
        title="Lead details"
        className="w-[95vw]! sm:max-w-[1400px]!"
      >
        {openLeadId && <SalesLeadDetail leadId={openLeadId} onClose={closeDrawer} />}
      </DrawerWithCloseButton>
    </div>
  )
}
